/**
 * SettingsPage — the workspace's spend limits (the required daily ad limit,
 * the optional monthly cap and managed-wallet caps; owner / admin only),
 * per-Holder defaults (autonomy), the ad-channel connections this workspace
 * runs campaigns through, and (platform admins only) opening AdOpt to an org
 * workspace's members and the approval that unlocks automatic mode.
 *
 * The OAuth round trip returns to THIS page with a `?connect=` result, which
 * is why the connections section lives here rather than on its own route.
 */

import { useEffect, useState } from 'react';
import { useArcheAdmin } from '@/auth/hooks/useArcheAdmin';
import { useAdoptSettings, usePutAdoptSettings } from '../hooks/useAdvertisingOptimization';
import { ChannelConnections } from '../components/ChannelConnections';
import { AutoUnlockCard } from '../components/AutoUnlockCard';
import { WorkspaceAccessCard } from '../components/WorkspaceAccessCard';
import { SpendLimitsCard } from '../components/SpendLimitsCard';
import type { AdoptAutonomyLevelDto } from '@/lib/api';

interface Props {
  holderId: string;
}

export function SettingsPage({ holderId }: Props) {
  const settingsQuery = useAdoptSettings(holderId);
  const putSettings = usePutAdoptSettings(holderId);
  // The approval route is requireAdmin server-side; this only hides the card.
  const isPlatformAdmin = useArcheAdmin();

  const [autonomy, setAutonomy] = useState<AdoptAutonomyLevelDto>('propose');

  useEffect(() => {
    if (settingsQuery.data) {
      setAutonomy(settingsQuery.data.defaultAutonomyLevel);
    }
  }, [settingsQuery.data]);

  if (settingsQuery.isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const current = settingsQuery.data;
  if (!current) return null;

  const save = (): void => {
    putSettings.mutate({
      version: current.version,
      defaultAutonomyLevel: autonomy,
    });
  };

  return (
    <div className="p-6 max-w-2xl mx-auto flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">Workspace-wide defaults for campaigns.</p>
      </header>

      <SpendLimitsCard holderId={holderId} />

      <section className="flex flex-col gap-2">
        <span className="text-sm font-medium">Default autonomy for new campaigns</span>
        <div className="flex gap-2">
          {(['manual', 'propose', 'auto'] as const).map((a) => (
            <button
              key={a}
              onClick={() => setAutonomy(a)}
              className={`px-3 py-1.5 rounded-md text-sm border capitalize ${
                autonomy === a
                  ? 'border-primary font-medium'
                  : 'border-border text-muted-foreground'
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </section>

      <ChannelConnections holderId={holderId} />

      {isPlatformAdmin && current.holderType === 'org' && (
        <WorkspaceAccessCard holderId={holderId} />
      )}

      {isPlatformAdmin && <AutoUnlockCard holderId={holderId} />}

      {putSettings.isError && (
        <div className="text-sm text-rose-600">
          Save failed{' '}
          {putSettings.error instanceof Error && putSettings.error.message.includes('409')
            ? '— settings changed elsewhere; reload and retry'
            : ''}
        </div>
      )}

      <div>
        <button
          onClick={save}
          disabled={putSettings.isPending}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          {putSettings.isPending ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
